import { useEffect, useState } from "react";
import { Eyebrow, Headline } from "@/components/Editorial";
import Button from "@/components/Button";
import TopWedge from "@/components/TopWedge";
import { FullPageLoader } from "@/components/Loader";
import { apiGet, apiPost } from "@/lib/api";
import { toast } from "@/components/Toast";
import { fmtDate, fmtMoney } from "@/lib/format";
import { C } from "@/lib/brand";

export default function Refer() {
  const [info, setInfo] = useState(null);
  const [credits, setCredits] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    const [i, c] = await Promise.all([
      apiGet("/consumer/referrals/mine").catch(() => null),
      apiGet("/consumer/credits").catch(() => null),
    ]);
    setInfo(i);
    setCredits(c);
  };
  useEffect(() => { load(); }, []);

  if (!info || !credits) return <FullPageLoader />;

  const copy = () => {
    navigator.clipboard.writeText(info.referralCode);
    toast("Code copied!", "success");
  };
  const copyLink = () => {
    const url = `${window.location.origin}/get-access?ref=${info.referralCode}`;
    navigator.clipboard.writeText(url);
    toast("Link copied!", "success");
  };
  const share = async () => {
    const url = `${window.location.origin}/get-access?ref=${info.referralCode}`;
    if (navigator.share) {
      try { await navigator.share({ title: "VOYA9", text: "Join me on VOYA9 — get $20 off your first month.", url }); } catch {}
    } else {
      copyLink();
    }
  };

  const invite = async (e) => {
    e.preventDefault();
    if (!inviteEmail || sending) return;
    setSending(true);
    try {
      const r = await apiPost("/consumer/referrals/invite", { method: "email", recipient: inviteEmail });
      toast(r.sentVia === "email" ? "Invite sent!" : "Link generated.", "success");
      setInviteEmail("");
      load();
    } catch (err) {
      toast(err.detail || "Could not send invite", "error");
    } finally {
      setSending(false);
    }
  };

  // Tiers — render config tier ladder if present, else generic 5-tier ladder
  const tiers = [
    { friends: 1, perFriend: 10, total: 10 },
    { friends: 3, perFriend: 15, total: 45 },
    { friends: 5, perFriend: 20, total: 100 },
    { friends: 10, perFriend: 25, total: 250 },
    { friends: 20, perFriend: 30, total: 600 },
  ];

  return (
    <div data-testid="refer-page" style={{ background: C.cream, paddingBottom: 80 }}>
      <section style={{ background: C.peach, color: C.peachDark, padding: "56px 24px 80px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <Eyebrow color={C.peachDark}>★ REFER A FRIEND</Eyebrow>
          <Headline before="GIVE $20." italic="get credit." color={C.peachDark} italicColor={C.peachDark} size={80} className="mt-4" />
          <p style={{ marginTop: 18, fontSize: 16, lineHeight: 1.55 }}>
            Friends save ${info.config?.refereeDiscountAmount || 20} on their first month. You earn credit when they activate.
          </p>
        </div>
      </section>

      <section style={{ padding: "52px 24px", position: "relative", background: C.cream }}>
        <TopWedge color={C.peach} dir="right" />
        <div style={{ maxWidth: 900, margin: "0 auto", position: "relative", zIndex: 2 }}>
          {/* Credit balance */}
          <div data-testid="credit-balance-card" style={{ background: C.ink, color: C.cream, padding: "28px 24px", display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.peach }}>YOUR BALANCE</div>
              <div style={{ marginTop: 8, fontSize: 44, fontWeight: 800, letterSpacing: "-0.04em" }}>{fmtMoney(credits.balance || 0)}</div>
            </div>
            <div style={{ fontSize: 11, color: "rgba(250,246,240,0.55)", letterSpacing: "0.05em", textAlign: "right" }}>
              Credits expire 90 days<br />after earning
            </div>
          </div>

          {/* Code */}
          <div style={{ marginTop: 16, padding: "20px 22px", background: C.bone, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray }}>YOUR CODE</div>
              <div data-testid="referral-code-display" style={{ marginTop: 4, fontFamily: 'JetBrains Mono, monospace', fontSize: 22, fontWeight: 700, letterSpacing: "0.05em" }}>
                {info.referralCode}
              </div>
            </div>
            <button onClick={copy} data-testid="referral-copy-btn" style={{ padding: "10px 16px", background: C.ink, color: C.cream, fontSize: 11, fontWeight: 800, letterSpacing: "0.18em" }}>
              COPY
            </button>
          </div>

          {/* Share buttons */}
          <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <Button variant="ghost" testId="refer-share-link-btn" onClick={copyLink}>COPY LINK</Button>
            <Button variant="ghost" testId="refer-share-share-btn" onClick={share}>SHARE</Button>
            <Button variant="primary" testId="refer-share-email-btn" onClick={() => document.getElementById("invite-email-input")?.focus()}>EMAIL →</Button>
          </div>

          {/* Email invite form */}
          <form onSubmit={invite} style={{ marginTop: 16, padding: 20, background: C.pure, border: `1.5px solid ${C.warmGray3}` }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray }}>INVITE BY EMAIL</div>
            <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
              <input
                id="invite-email-input"
                data-testid="invite-email-input"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="friend@email.com"
                style={{ flex: 1, padding: "12px 14px", background: C.cream, border: `1.5px solid ${C.warmGray3}`, fontSize: 14, fontFamily: "inherit", outline: "none" }}
              />
              <Button type="submit" variant="primary" testId="invite-send-btn" disabled={sending}>{sending ? "…" : "SEND →"}</Button>
            </div>
          </form>

          {/* Reward ladder */}
          <div style={{ marginTop: 32 }}>
            <Eyebrow>REWARD LADDER</Eyebrow>
            <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }} data-testid="reward-ladder">
              {tiers.map((t, i) => {
                const reached = (info.stats?.totalActivated || 0) >= t.friends;
                const current = (info.currentTier?.friends || 0) === t.friends;
                return (
                  <div key={i} data-testid={`tier-row-${i}`} style={{ display: "flex", alignItems: "center", gap: 16, padding: "16px 18px", background: current ? C.peach : reached ? C.sage : C.bone, color: reached ? C.sageDark : current ? C.peachDark : C.warmGray }}>
                    <div style={{ fontFamily: '"Instrument Serif", serif', fontStyle: "italic", fontSize: 30, minWidth: 60 }}>{t.friends}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.05em" }}>{t.friends} FRIEND{t.friends > 1 ? "S" : ""}</div>
                      <div style={{ fontSize: 12 }}>${t.perFriend} per activation</div>
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 800 }}>${t.total}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Friends */}
          <div style={{ marginTop: 32 }}>
            <Eyebrow>RECENT INVITES</Eyebrow>
            <div style={{ marginTop: 16, background: C.pure, border: `1.5px solid ${C.warmGray3}` }} data-testid="friends-list">
              {(info.friends || []).length === 0 && (
                <div style={{ padding: 32, textAlign: "center", color: C.warmGray, fontSize: 13 }}>No invites yet.</div>
              )}
              {(info.friends || []).map((f, i) => {
                // BE ships four statuses since the May 2026 invite
                // tracking update: "sent" (emailed, not signed up),
                // "signed_up" (account exists), "activated" (paid),
                // "fraud" (flagged). Pre-update rows omit `sent`.
                const isSent = f.status === "sent";
                const isActivated = f.status === "activated";
                const isFraud = f.status === "fraud";
                // Display label: prefer initial+masked email for sent
                // invites (we don't have a real name yet), and fall
                // back to first-name initial for everything else.
                const titleLabel = isSent
                  ? (f.recipientMasked || "Invite sent")
                  : (f.firstNameInitial || f.recipientMasked || "—");
                // Date label varies by status. "sent" rows use
                // sentAt/createdAt; "signed_up" use signedUpAt;
                // "activated" use activatedAt.
                const dateValue = isSent
                  ? (f.sentAt || f.createdAt || f.invitedAt)
                  : (isActivated ? f.activatedAt : f.signedUpAt);
                const statusLabel = isSent
                  ? `Invited ${fmtDate(dateValue)}`
                  : isFraud
                    ? "Flagged"
                    : isActivated
                      ? `Activated ${fmtDate(dateValue)}`
                      : `Signed up ${fmtDate(dateValue)}`;
                const rightLabel = isActivated
                  ? `+${fmtMoney(f.creditEarned || 0)}`
                  : isSent
                    ? "Sent"
                    : isFraud
                      ? "—"
                      : "Pending";
                const rightColor = isActivated
                  ? C.sageDeep
                  : isFraud
                    ? C.coral
                    : C.warmGray;
                return (
                  <div
                    key={i}
                    data-testid={`friend-row-${i}`}
                    data-status={f.status || "unknown"}
                    style={{
                      padding: "14px 18px",
                      borderBottom: i < info.friends.length - 1 ? `1px solid ${C.warmGray4}` : "none",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      fontSize: 13,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, wordBreak: "break-word" }}>{titleLabel}</div>
                      <div style={{ fontSize: 11, color: C.warmGray, marginTop: 2 }}>
                        {statusLabel}
                      </div>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: rightColor, whiteSpace: "nowrap" }}>
                      {rightLabel}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <p style={{ marginTop: 20, fontSize: 11, color: C.warmGray, lineHeight: 1.55 }}>
            Credits apply automatically to your invoices. Maximum {fmtMoney(info.config?.monthlyCap || 150)} per month, capped at 75% of subtotal. Earned credits expire {info.config?.creditExpirationDays || 90} days after earning.
          </p>
        </div>
      </section>
    </div>
  );
}
